import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, EffectFade } from "swiper/modules";
import { api } from '../../services/api';
import { useContentItem } from '../../hooks/useContent';
import { getImageUrl } from '../../utils/imageUrl';
import { formatTourDate, tourHasPackages } from '../../utils/text';
import SidebarWidgets from '../Common/SidebarWidgets';
import TourBookingForm from '../Contact/TourBookingForm';

function TourDetailsMain() {
    const { id } = useParams();
    const { item: tour, loading, error } = useContentItem(api.getTour, id);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeTab, setActiveTab] = useState(null);

    useEffect(() => {
        if (loading || !tour) return;
        if (window.location.hash === '#book') {
            const el = document.getElementById('book');
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
            }
        }
    }, [loading, tour]);

    if (loading) {
        return <div className="container py-5 text-center">Loading tour...</div>;
    }

    if (error || !tour) {
        return <div className="container py-5 text-center text-danger">{error || 'Tour not found!'}</div>;
    }

    const images = (tour.images && tour.images.length > 0)
        ? tour.images.map((img) => getImageUrl(img, 'tour'))
        : [getImageUrl(tour.bannerImg || tour.image, 'tour')].filter(Boolean);

    const itinerary = tour.itinerary && tour.itinerary.length > 0 ? tour.itinerary : [];
    const days = itinerary.map((day) => ({
        id: `day-tab${day.day_number}`,
        label: day.title || `Day ${String(day.day_number).padStart(2, '0')}`,
        description: day.description,
        image: day.image ? getImageUrl(day.image, 'tour') : null,
    }));

    const inclusionRows = Array.isArray(tour.inclusions) ? tour.inclusions : [];
    const includedItems = inclusionRows.filter(
        (item) => item.type === 'included' || item.type === 'highlight'
    );
    const excludedItems = inclusionRows.filter((item) => item.type === 'excluded');
    const hasInclusions = includedItems.length > 0 || excludedItems.length > 0;
    const packages = Array.isArray(tour.packages) ? tour.packages : [];
    const hasPackages = packages.length > 0 || tourHasPackages(tour);

    const currentTab = activeTab || (days[0]?.id ?? null);

    const mapSrc = tour.map_embed || null;

    return (
        <section className="space">
            <div className="container shape-mockup-wrap">
                <div className="row">
                    <div className="col-xxl-8 col-lg-7">
                        <div className="tour-page-single">
                            {images.length > 0 && (
                            <div className="slider-area tour-slider1">
                                <Swiper
                                    modules={[Navigation, Thumbs, EffectFade]}
                                    effect="fade"
                                    loop={images.length > 1}
                                    spaceBetween={10}
                                    navigation={{
                                        prevEl: ".slider-prev",
                                        nextEl: ".slider-next",
                                    }}
                                    thumbs={{ swiper: thumbsSwiper }}
                                    className="swiper th-slider mb-4"
                                    id="tourSlider4"
                                >
                                    {images.map((img, index) => (
                                        <SwiperSlide key={index}>
                                            <div className="tour-slider-img">
                                                <img src={img} alt={`Slide ${index + 1}`} />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                {images.length > 1 && (
                                <Swiper
                                    modules={[Navigation, Thumbs]}
                                    loop={true}
                                    spaceBetween={25}
                                    slidesPerView={3}
                                    watchSlidesProgress
                                    onSwiper={setThumbsSwiper}
                                    className="swiper th-slider tour-thumb-slider"
                                >
                                    {images.map((img, index) => (
                                        <SwiperSlide key={index}>
                                            <div className="tour-slider-img">
                                                <img src={img} alt={`Thumbnail ${index + 1}`} />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                )}
                                <button
                                    data-slider-prev="#tourSlider4"
                                    className="slider-arrow style3 slider-prev"
                                >
                                    <img src="/assets/img/icon/hero-arrow-left.svg" alt="" />
                                </button>
                                <button
                                    data-slider-next="#tourSlider4"
                                    className="slider-arrow style3 slider-next"
                                >
                                    <img src="/assets/img/icon/hero-arrow-right.svg" alt="" />
                                </button>
                            </div>
                            )}
                            <div className="page-content">
                                <div className="page-meta mb-45">
                                    {(tour.categories || []).length > 0 ? (
                                        tour.categories.map((cat) => (
                                            <Link className="page-tag mr-5" to="/tour" key={cat.id}>
                                                {cat.name}
                                            </Link>
                                        ))
                                    ) : (
                                        <Link className="page-tag mr-5" to="/tour">
                                            Featured
                                        </Link>
                                    )}
                                    <span className="ratting">
                                        <i className="fa-sharp fa-solid fa-star" />
                                        <span>{tour.rating || 4.8}</span>
                                    </span>
                                </div>
                                <h2 className="box-title">
                                    {tour.title}
                                </h2>
                                {tour.price && (
                                    <h4 className="tour-price">
                                        <span className="currency">{tour.price}</span>
                                        {hasPackages ? null : '/Person'}
                                    </h4>
                                )}
                                {hasPackages ? (
                                    <div className="twa-tour-packages mb-40">
                                        <h3 className="box-title">Packages</h3>
                                        <ul className="twa-tour-packages__list">
                                            {packages.map((pkg) => (
                                                <li key={pkg.id}>
                                                    <span className="twa-tour-packages__name">
                                                        <strong>{pkg.title}</strong>
                                                        {pkg.description ? (
                                                            <span className="twa-tour-packages__note"> — {pkg.description}</span>
                                                        ) : null}
                                                    </span>
                                                    <span className="twa-tour-packages__price">{pkg.price}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                                {tour.short_description && (
                                    <div
                                        className="box-text mb-30 cms-rich-content"
                                        dangerouslySetInnerHTML={{ __html: tour.short_description }}
                                    />
                                )}
                                {(tour.departures || []).length > 0 && (
                                    <div className="twa-tour-dates mb-40">
                                        <h3 className="box-title">Upcoming dates</h3>
                                        <ul className="twa-tour-dates__list">
                                            {tour.departures.map((dep) => (
                                                <li key={dep.id} className={dep.is_full ? 'is-full' : ''}>
                                                    <span className="twa-tour-dates__when">
                                                        {dep.label ? <strong>{dep.label} · </strong> : null}
                                                        {formatTourDate(dep.start_date)}
                                                        {dep.end_date && dep.end_date !== dep.start_date
                                                            ? ` – ${formatTourDate(dep.end_date)}`
                                                            : ''}
                                                    </span>
                                                    <span className={`twa-tour-dates__slots${dep.is_full ? ' is-full' : ''}`}>
                                                        {dep.is_full
                                                            ? 'Sold out'
                                                            : `${dep.available_slots} of ${dep.total_slots} slots left`}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {tour.description && (
                                    <div
                                        className="box-text mb-50 cms-rich-content"
                                        dangerouslySetInnerHTML={{ __html: tour.description }}
                                    />
                                )}
                                {!tour.description && !tour.short_description && (
                                    <p className="box-text mb-50">Tour details will appear here once updated from CMS.</p>
                                )}
                                {(tour.location || tour.country || tour.duration_days) && (
                                    <>
                                        <h2 className="box-title">Basic Information</h2>
                                        <div className="destination-checklist mb-50">
                                            <div className="checklist style2">
                                                <ul>
                                                    {tour.location && <li>Location</li>}
                                                    {tour.country && <li>Country</li>}
                                                    {tour.duration_days && <li>Duration</li>}
                                                    {tour.duration_nights && <li>Nights</li>}
                                                </ul>
                                            </div>
                                            <div className="checklist style2">
                                                <ul>
                                                    {tour.location && <li>{tour.location}</li>}
                                                    {tour.country && <li>{tour.country}</li>}
                                                    {tour.duration_days && <li>{tour.duration_days} Days</li>}
                                                    {tour.duration_nights && <li>{tour.duration_nights} Nights</li>}
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {days.length > 0 ? (
                                <>
                                <h3 className="page-title mt-50 mb-0">Tour Plan</h3>
                                <div>
                                    <ul className="nav nav-tabs tour-tab mt-10" role="tablist">
                                        {days.map((day) => (
                                            <li className="nav-item" key={day.id} role="presentation">
                                                <button
                                                    className={`nav-link ${currentTab === day.id ? "active" : ""}`}
                                                    onClick={() => setActiveTab(day.id)}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    {day.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="tab-content">
                                        {days.map((day) => {
                                            const bullets = day.description
                                                ? day.description.split('\n').map((l) => l.trim()).filter(Boolean)
                                                : ['Itinerary details coming soon.'];
                                            return (
                                            <div
                                                key={day.id}
                                                className={`tab-pane fade ${currentTab === day.id ? "show active" : ""}`}
                                                role="tabpanel"
                                            >
                                                <div className="tour-grid-plan">
                                                    {day.image && (
                                                        <div className="mb-4">
                                                            <img src={day.image} alt={day.label} className="w-100 rounded" />
                                                        </div>
                                                    )}
                                                    <div className="checklist">
                                                        <ul>
                                                            {bullets.map((item, index) => (
                                                                <li key={index}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                </>
                                ) : null}

                                {hasInclusions ? (
                                    <div className="twa-tour-inclusions mt-50 mb-50">
                                        <h2 className="box-title">Included and Excluded</h2>
                                        <div className="destination-checklist twa-inclusion-grid">
                                            {includedItems.length > 0 ? (
                                                <div className="checklist style2 style4">
                                                    <h4 className="twa-inclusion-heading">Included</h4>
                                                    <ul>
                                                        {includedItems.map((item) => (
                                                            <li key={`inc-${item.id}`}>{item.item}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : null}
                                            {excludedItems.length > 0 ? (
                                                <div className="checklist style5">
                                                    <h4 className="twa-inclusion-heading">Excluded</h4>
                                                    <ul>
                                                        {excludedItems.map((item) => (
                                                            <li key={`exc-${item.id}`}>{item.item}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : null}

                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-4 col-lg-5">
                        <div className="twa-booking-panel mb-40" id="book">
                            <TourBookingForm
                                initialTourId={tour.id}
                                lockedTourTitle={tour.title}
                                departures={tour.departures || []}
                                packages={packages}
                                adultMinAge={tour.adult_min_age}
                                childMaxAge={tour.child_max_age}
                                compact
                                lockTour
                                source="tour_page"
                                title="Reserve This Tour"
                            />
                        </div>
                        <SidebarWidgets categoryType="tour" />
                    </div>
                </div>
                {mapSrc && (
                <div className="location-map">
                    <h3 className="page-title mt-45 mb-30">Location</h3>
                    <div className="contact-map style3">
                        <iframe
                            src={mapSrc}
                            allowFullScreen=""
                            loading="lazy"
                            title="Tour location map"
                        />
                        <div className="contact-icon">
                            <img src="/assets/img/icon/location-dot3.svg" alt="" />
                        </div>
                    </div>
                </div>
                )}
            </div>
        </section>
    )
}

export default TourDetailsMain
